import * as React from 'react';

import { cn } from '../../lib/utils';
import {
  formatAbsoluteWithZones,
  formatDate,
  getTimeAgo,
} from '../../utils/dateHelpers';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from './tooltip';

type TimestampFormat =
  | 'relative'
  | 'absolute'
  | 'date-only'
  | 'time-only'
  | 'future';

export interface TimestampProps
  extends Omit<React.ComponentProps<'time'>, 'dateTime' | 'children' | 'title'> {
  /** ISO string or Date. Null/undefined renders the fallback. */
  value: string | Date | null | undefined;
  /**
   * Visible format:
   * - `relative` (default): "2 hours ago" / "in 2 hours"
   * - `absolute`: "23 Apr 2026, 11:15"
   * - `date-only`: "23 Apr 2026"
   * - `time-only`: "11:15" — use when the date is already shown in a
   *   group header / nearby column and the cell only needs the clock.
   * - `future`: like relative, but past values are prefixed with
   *   "Overdue · ". Use for fields that SHOULD be a future time
   *   (next-run, scheduled-at) so an unfired schedule reads clearly.
   */
  format?: TimestampFormat;
  /** What to show when value is missing or invalid. Defaults to "—". */
  fallback?: React.ReactNode;
  /** If true, suppress the hover tooltip with absolute UTC + local. */
  hideTooltip?: boolean;
  className?: string;
}

const toDate = (value: string | Date | null | undefined): Date | null => {
  if (value == null) return null;
  const date = value instanceof Date ? value : new Date(value);
  return isNaN(date.getTime()) ? null : date;
};

const renderVisible = (date: Date, format: TimestampFormat): string => {
  switch (format) {
    case 'absolute': {
      const datePart = date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
      const timePart = date.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
      return `${datePart}, ${timePart}`;
    }
    case 'date-only':
      return formatDate(date.toISOString());
    case 'time-only':
      return date.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
    case 'future': {
      // For fields that should be in the future. ``getTimeAgo`` already
      // handles future ("in X") vs past ("X ago"); we add the
      // ``Overdue ·`` prefix for past values so an overdue scheduled
      // run is unambiguous in tables labelled "Next run".
      const phrase = getTimeAgo(date.toISOString());
      const isPast = date.getTime() < Date.now();
      return isPast ? `Overdue · ${phrase}` : phrase;
    }
    case 'relative':
    default:
      return getTimeAgo(date.toISOString());
  }
};

/**
 * Canonical timestamp display.
 *
 * Renders a value (relative by default) with a hover tooltip showing the
 * absolute UTC + local time. Use anywhere a record's "created at" / "updated at"
 * / "last run" / etc. is shown.
 *
 * Replaces ad-hoc `getTimeAgo(...)` / `toLocaleString(...)` calls so users
 * always have an unambiguous read on the underlying time.
 */
function Timestamp({
  value,
  format = 'relative',
  fallback = '—',
  hideTooltip,
  className,
  ...timeProps
}: TimestampProps) {
  const date = toDate(value);

  if (!date) {
    return <span className={className}>{fallback}</span>;
  }

  const visible = renderVisible(date, format);
  const iso = date.toISOString();

  const inner = (
    <time
      dateTime={iso}
      className={cn(hideTooltip ? '' : 'cursor-help', className)}
      {...timeProps}
    >
      {visible}
    </time>
  );

  if (hideTooltip) return inner;

  const tooltipText = formatAbsoluteWithZones(date) ?? iso;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{inner}</TooltipTrigger>
      <TooltipContent>{tooltipText}</TooltipContent>
    </Tooltip>
  );
}

export { Timestamp };
